import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Eye, EyeOff } from 'lucide-react'
import { auth } from '@/lib/firebase'
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth'
import { handleUserData } from '@/lib/firebase'
import { useAnalytics } from '@/hooks/useAnalytics'
import { logAuthAttempt } from '@/lib/securityLogging'
import { getDeviceInfo, logDeviceInfoDebug } from '@/lib/deviceInfo'

interface LoginSlideProps {
  onSuccess: () => void
  onFormValidChange: (isValid: boolean) => void
  onPasswordResetChange?: (isInPasswordReset: boolean, isConfirmation: boolean) => void
}

export function LoginSlide({ onSuccess, onFormValidChange, onPasswordResetChange }: LoginSlideProps) {
  const { t } = useTranslation()
  const { trackSignIn } = useAnalytics()
  
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [resetEmail, setResetEmail] = useState('')
  const [resetStep, setResetStep] = useState<'input' | 'confirmation'>('input')
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (error) setError(null)
  }

  const isFormValid = (): boolean => {
    return !!(formData.email.trim() && formData.password.trim())
  }

  // Update parent when form validity changes
  useEffect(() => {
    if (showPasswordReset) {
      if (resetStep === 'confirmation') {
        onFormValidChange(true)
      } else {
        onFormValidChange(!!resetEmail.trim())
      }
    } else {
      onFormValidChange(isFormValid())
    }
  }, [formData.email, formData.password, resetEmail, showPasswordReset, resetStep, onFormValidChange])

  // Notify parent about password reset state
  useEffect(() => {
    onPasswordResetChange?.(showPasswordReset, resetStep === 'confirmation')
  }, [showPasswordReset, resetStep, onPasswordResetChange])

  const getErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return t('auth.invalidCredentials')
      case 'auth/invalid-email':
        return t('auth.invalidEmail')
      default:
        return t('auth.genericError')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email.trim())) {
      setError(t('auth.invalidEmail'))
      return
    }
    
    setLoading(true)
    setError(null)

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      )
      
      // Get and log device info on login
      const deviceInfo = await getDeviceInfo()
      console.log('[LoginSlide] Login - Device Info:', deviceInfo)
      logDeviceInfoDebug(deviceInfo)
      
      // Track sign in
      await trackSignIn('email', 'onboarding')
      
      // Log successful login
      await logAuthAttempt('login', formData.email, userCredential.user.uid)
      
      // Update lastLogin
      await handleUserData(userCredential.user, {})
      
      // Complete onboarding
      onSuccess()
    } catch (error: any) {
      setError(getErrorMessage(error.code))
    } finally {
      setLoading(false)
    }
  }

  const handleResetSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(resetEmail.trim())) {
      setError(t('auth.invalidEmail'))
      return
    }

    setLoading(true)
    setError(null)

    try {
      await sendPasswordResetEmail(auth, resetEmail.trim())
      console.log('Password reset email sent to:', resetEmail)
      setResetStep('confirmation')
    } catch (err: any) {
      console.error('Password reset error:', err)
      // Show success anyway for security
      setResetStep('confirmation')
    } finally {
      setLoading(false)
    }
  }


  // Password Reset View
  if (showPasswordReset) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 py-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-foreground">
              {resetStep === 'input' ? 'Password Reset 🤔' : 'Password Reset 🫡'}
            </h2>
          </div>

          {resetStep === 'input' ? (
            <form id="password-reset-form" onSubmit={handleResetSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                Enter your email and we'll send a reset email if an account exists:
              </p>

              <div className="space-y-2">
                <Input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => {
                    setResetEmail(e.target.value)
                    if (error) setError(null)
                  }}
                  placeholder="your@email.com"
                  required
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[var(--card-radius)]">
                  {error}
                </div>
              )}
            </form>
          ) : (
            <div className="space-y-6">
              <p className="text-base text-foreground leading-relaxed text-center">
                Sent reset email to {resetEmail} if that account exists
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Login View
  return (
    <div className="h-full flex flex-col items-center justify-center px-6 py-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground">
            {t('auth.login')}
          </h2>
        </div>

        <form id="login-form" onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('auth.email')}
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                required
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t('auth.password')}
              </label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

          {/* Error Message */}
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-[var(--card-radius)]">
              {error}
            </div>
          )}
        </form>
        
        {/* Forgot Password Link - Moved outside form to prevent iOS autofill from clicking it */}
        <button
          type="button"
          onClick={() => setShowPasswordReset(true)}
          className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2 mt-4"
        >
          Forgot password?
        </button>
      </div>
    </div>
  )
}
